import { useCallback, useMemo, useState } from 'react';
import type { FunctionComponent, ReactElement } from 'react';

import { COMMAND_PALETTE } from '../../../shared/featureFlags/flagNames';
import { useIsFeatureEnabled } from '../../helpers/features/featureFlags';

import { CommandPaletteContext, useCommandPaletteContext } from './context';
import useCmdKPress from './hooks/useCmdKPress';
import Palette from './Palette';
import type { CommandPaletteContextType } from './context';

// Export the context hook for use in other components
export { useCommandPaletteContext };

/**
 * The entrypoint for the CommandPalette.
 * This component is responsible for rendering the input field
 * and the panel that appears when the input field is focused.
 *
 * Note that we set up Context and wrap the entire application in this,
 * so that we are able to open/close the palette from anywhere in the app.
 * In practice, we only do this in one place, which is in the AccountDropdown in the Nav.
 */
export const CommandPalette: FunctionComponent = ({ children }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const onHide = useCallback(() => setIsOpen(false), []);
  const onShow = useCallback(() => setIsOpen(true), []);
  const toggleOpen = useCallback(() => setIsOpen(!isOpen), [isOpen]);

  //  Listen for Cmd-K press to toggle the palette.
  useCmdKPress(toggleOpen);

  //  We store this in a context value
  const contextValue = useMemo<CommandPaletteContextType>(() => ({
    isOpen,
    onHide,
    onShow,
  }), [isOpen, onHide, onShow]);

  return (
    <CommandPaletteContext.Provider value={contextValue}>
      {children}
      {isOpen && <Palette onHide={onHide} isOpen={isOpen} />}
    </CommandPaletteContext.Provider>
  );
};

//  Check if the flag is available before rendering the CommandPalette. Typed this way because FunctionComponent is wrong.
const CommandPaletteGate: FunctionComponent = ({ children }) => {
  const isEnabled = useIsFeatureEnabled(COMMAND_PALETTE);
  //  Just render the children if the feature is not enabled.
  if (!isEnabled) {
    return children as ReactElement;
  }
  return <CommandPalette>{children}</CommandPalette>;
};

export default CommandPaletteGate;
